import React, { useState } from 'react';
import { supabase } from '../supabase';

function StitchAdmin({ onClose, onSave, editingStitch }) {
  const [formData, setFormData] = useState({
    id: editingStitch?.id || "",
    name: editingStitch?.name || "",
    abbreviation: editingStitch?.abbreviation || "",
    description: editingStitch?.description || "",
    instructions: editingStitch?.instructions || [""],
    image: editingStitch?.image || "",
    tutorialUrl: editingStitch?.tutorialUrl || "",
    difficulty: editingStitch?.difficulty || "Beginner",
    category: editingStitch?.category || "Foundational"
  });

  const [error, setError] = useState("");

  const colors = {
    sageGreen: "#a3b18a",
    dustyRust: "#c1795b",
    mustard: "#d4a574",
    bgCream: "#faf8f5",
    borderGreige: "#e8e0d5",
    charcoal: "#2a2a2a",
    darkGray: "#6b6b6b",
    brownGray: "#8b7355",
  };

  const fonts = {
    display: "'Pacifico', cursive",
    body: "'Karla', sans-serif",
  };

  const addInstruction = () => {
    setFormData({
      ...formData,
      instructions: [...formData.instructions, ""]
    });
  };

  const updateInstruction = (index, value) => {
    const updated = [...formData.instructions];
    updated[index] = value;
    setFormData({ ...formData, instructions: updated });
  };

  const removeInstruction = (index) => {
    setFormData({
      ...formData,
      instructions: formData.instructions.filter((_, i) => i !== index)
    });
  };

const handleSubmit = async () => {
  // Validation
  if (!formData.id || !formData.name || !formData.abbreviation) {
    setError("ID, Name, and Abbreviation are required");
    return;
  }

  const filteredInstructions = formData.instructions.filter(inst => inst.trim() !== "");
  
  if (filteredInstructions.length === 0) {
    setError("At least one instruction is required");
    return;
  }

  try {
    // Transform to Supabase format
    const supabaseData = {
      id: formData.id,
      name: formData.name,
      abbreviation: formData.abbreviation,
      description: formData.description,
      instructions: filteredInstructions,
      image: formData.image,
      tutorial_url: formData.tutorialUrl,
      difficulty: formData.difficulty,
      category: formData.category
    };

    if (editingStitch) {
      // UPDATE
      const { error } = await supabase
        .from('stitches')
        .update(supabaseData)
        .eq('id', formData.id);
      
      if (error) throw error;
      alert("Stitch updated!");
    } else {
      // INSERT - Get next sort_order
      const { data: maxData, error: maxError } = await supabase
        .from('stitches')
        .select('sort_order')
        .order('sort_order', { ascending: false })
        .limit(1);
      
      if (maxError) throw maxError;
      
      const nextSortOrder = maxData && maxData.length > 0 
        ? (maxData[0].sort_order || 0) + 1 
        : 1;
      
      supabaseData.sort_order = nextSortOrder;
      
      const { error } = await supabase
        .from('stitches')
        .insert([supabaseData]);
      
      if (error) throw error;
      alert("Stitch added!");
    }

    onSave(); // Trigger refresh
    onClose();
    
  } catch (err) {
    console.error('Error saving stitch:', err);
    setError(`Failed to save: ${err.message}`);
  }
};

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: "2rem",
        overflow: "auto",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: "20px",
          maxWidth: "700px",
          width: "100%",
          maxHeight: "90vh",
          overflow: "auto",
          padding: "2.5rem",
          fontFamily: fonts.body,
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: "2rem" }}>
          <h2
            style={{
              fontSize: "2rem",
              fontFamily: fonts.display,
              color: colors.charcoal,
              marginBottom: "0.5rem",
            }}
          >
            {editingStitch ? "Edit Stitch" : "Add New Stitch"}
          </h2>
          <p style={{ color: colors.darkGray, fontSize: "0.95rem" }}>
            {editingStitch ? "Update stitch details" : "Add a stitch to the library"}
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div
            style={{
              padding: "1rem",
              background: "#fff3cd",
              border: "2px solid #ffc107",
              borderRadius: "8px",
              marginBottom: "1.5rem",
              color: "#856404",
            }}
          >
            {error}
          </div>
        )}

        {/* Form Fields */}
        <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
          
          {/* ID (slug) */}
          <div>
            <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "600", color: colors.brownGray, fontSize: "0.9rem" }}>
              ID (slug) *
            </label>
            <input
              type="text"
              value={formData.id}
              onChange={(e) => setFormData({ ...formData, id: e.target.value })}
              placeholder="e.g., sc, dc, shell-stitch"
              disabled={!!editingStitch}
              style={{
                width: "100%",
                padding: "0.875rem",
                border: `2px solid ${colors.borderGreige}`,
                borderRadius: "8px",
                fontSize: "1rem",
                outline: "none",
                background: editingStitch ? colors.bgCream : "#fff",
                cursor: editingStitch ? "not-allowed" : "text"
              }}
            />
            <p style={{ fontSize: "0.75rem", color: colors.darkGray, marginTop: "0.25rem", fontStyle: "italic" }}>
              Lowercase, use hyphens for spaces. Cannot be changed after creation.
            </p>
          </div>

          {/* Name */}
          <div>
            <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "600", color: colors.brownGray, fontSize: "0.9rem" }}>
              Stitch Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Single Crochet"
              style={{
                width: "100%",
                padding: "0.875rem",
                border: `2px solid ${colors.borderGreige}`,
                borderRadius: "8px",
                fontSize: "1rem",
                outline: "none"
              }}
            />
          </div>

          {/* Abbreviation */}
          <div>
            <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "600", color: colors.brownGray, fontSize: "0.9rem" }}>
              Abbreviation *
            </label>
            <input
              type="text"
              value={formData.abbreviation}
              onChange={(e) => setFormData({ ...formData, abbreviation: e.target.value })}
              placeholder="e.g., sc"
              style={{
                width: "100%",
                padding: "0.875rem",
                border: `2px solid ${colors.borderGreige}`,
                borderRadius: "8px",
                fontSize: "1rem",
                outline: "none"
              }}
            />
          </div>

          {/* Description */}
          <div>
            <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "600", color: colors.brownGray, fontSize: "0.9rem" }}>
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of the stitch..."
              rows={3}
              style={{
                width: "100%",
                padding: "0.875rem",
                border: `2px solid ${colors.borderGreige}`,
                borderRadius: "8px",
                fontSize: "1rem",
                outline: "none",
                fontFamily: fonts.body,
                resize: "vertical"
              }}
            />
          </div>

          {/* Difficulty */}
          <div>
            <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "600", color: colors.brownGray, fontSize: "0.9rem" }}>
              Difficulty *
            </label>
            <select
              value={formData.difficulty}
              onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
              style={{
                width: "100%",
                padding: "0.875rem",
                border: `2px solid ${colors.borderGreige}`,
                borderRadius: "8px",
                fontSize: "1rem",
                outline: "none"
              }}
            >
              <option>Beginner Friendly</option>
              <option>Beginner</option>
              <option>Intermediate</option>
              <option>Advanced</option>
            </select>
          </div>

          {/* Category */}
          <div>
            <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "600", color: colors.brownGray, fontSize: "0.9rem" }}>
              Category *
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              style={{
                width: "100%",
                padding: "0.875rem",
                border: `2px solid ${colors.borderGreige}`,
                borderRadius: "8px",
                fontSize: "1rem",
                outline: "none"
              }}
            >
              <option>Foundational</option>
              <option>Textured</option>
              <option>Decorative</option>
              <option>Special</option>
            </select>
          </div>

          {/* Instructions */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
              <label style={{ fontWeight: "600", color: colors.brownGray, fontSize: "0.9rem" }}>
                Instructions *
              </label>
              <button
                type="button"
                onClick={addInstruction}
                style={{
                  padding: "0.5rem 1rem",
                  background: colors.sageGreen,
                  color: "#fff",
                  border: "none",
                  borderRadius: "50px",
                  fontSize: "0.85rem",
                  fontWeight: "600",
                  cursor: "pointer"
                }}
              >
                + Add Step
              </button>
            </div>
            
            {formData.instructions.map((instruction, idx) => (
              <div key={idx} style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem" }}>
                <input
                  type="text"
                  value={instruction}
                  onChange={(e) => updateInstruction(idx, e.target.value)}
                  placeholder={`Step ${idx + 1}`}
                  style={{
                    flex: 1,
                    padding: "0.75rem",
                    border: `2px solid ${colors.borderGreige}`,
                    borderRadius: "8px",
                    fontSize: "0.95rem",
                    outline: "none"
                  }}
                />
                {formData.instructions.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeInstruction(idx)}
                    style={{
                      padding: "0.75rem",
                      background: colors.dustyRust,
                      color: "#fff",
                      border: "none",
                      borderRadius: "8px",
                      fontSize: "1rem",
                      cursor: "pointer",
                      fontWeight: "bold",
                      width: "40px"
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Image URL */}
          <div>
            <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "600", color: colors.brownGray, fontSize: "0.9rem" }}>
              Image URL
            </label>
            <input
              type="text"
              value={formData.image}
              onChange={(e) => setFormData({ ...formData, image: e.target.value })}
              placeholder="/images/stitches/single-crochet.jpg"
              style={{
                width: "100%",
                padding: "0.875rem",
                border: `2px solid ${colors.borderGreige}`,
                borderRadius: "8px",
                fontSize: "1rem",
                outline: "none"
              }}
            />
          </div>

          {/* Tutorial URL */}
          <div>
            <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "600", color: colors.brownGray, fontSize: "0.9rem" }}>
              Tutorial URL (YouTube)
            </label>
            <input
              type="url"
              value={formData.tutorialUrl}
              onChange={(e) => setFormData({ ...formData, tutorialUrl: e.target.value })}
              placeholder="https://youtube.com/..."
              style={{
                width: "100%",
                padding: "0.875rem",
                border: `2px solid ${colors.borderGreige}`,
                borderRadius: "8px",
                fontSize: "1rem",
                outline: "none"
              }}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: "flex", gap: "1rem", marginTop: "2rem" }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: "1rem",
              background: "#fff",
              color: colors.brownGray,
              border: `2px solid ${colors.borderGreige}`,
              borderRadius: "50px",
              fontSize: "1rem",
              fontWeight: "600",
              cursor: "pointer"
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            style={{
              flex: 2,
              padding: "1rem",
              background: `linear-gradient(135deg, ${colors.sageGreen} 0%, #8e9775 100%)`,
              color: "#fff",
              border: "none",
              borderRadius: "50px",
              fontSize: "1rem",
              fontWeight: "600",
              cursor: "pointer"
            }}
          >
            {editingStitch ? "Update Stitch" : "Add Stitch"}
          </button>
        </div>

        {/* Close X */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "1.5rem",
            right: "1.5rem",
            background: "transparent",
            border: "none",
            fontSize: "1.5rem",
            color: colors.brownGray,
            cursor: "pointer",
            padding: "0.5rem",
            lineHeight: 1
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}

export default StitchAdmin;
